// src/pages/ChangePasswordPage.tsx
import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { ArrowLeft, Eye, EyeOff } from 'lucide-react'
import { supabase } from '../lib/supabaseClient'

export default function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState<string>()
  const [message, setMessage] = useState<string>()
  const [loading, setLoading] = useState(false)
  
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(undefined)
    setMessage(undefined)
    
    // Validation
    if (newPassword !== confirmPassword) {
      setError("New passwords don't match")
      return
    }
    
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters")
      return
    }
    
    setLoading(true)
    
    try {
      // First, update with the current password to verify it
      const { error: passwordError } = await supabase.auth.updateUser({
        password: newPassword,
      }, {
        // Pass current password in auth options (for security)
        // Note: Since this is an auth flow change, you might need additional code based on Supabase version
        // If this doesn't work properly, you may need to implement a custom auth call 
      })
      
      if (passwordError) {
        setError(passwordError.message)
      } else {
        setMessage('Password updated successfully!')
        // Clear form
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        // Navigate back to settings after a delay
        setTimeout(() => navigate('/app/settings'), 1500)
      }
    } catch (err) {
      console.error('Password update error:', err)
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Atmospheric lighting effects */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full bg-primary-500/15 blur-[120px] -z-10"></div>
      <div className="absolute bottom-0 left-0 w-[800px] h-[800px] rounded-full bg-gray-300/20 blur-[120px] -z-10"></div>
      
      <div className="w-full max-w-md mb-6 relative z-10">
        <Button 
          as={Link} 
          to="/app/settings"
          variant="ghost" 
          className="flex items-center text-gray-700 hover:text-gray-950 transition-colors"
        >
          <ArrowLeft size={18} className="mr-1" />
          Back to Settings
        </Button>
      </div>

      <Card className="w-full max-w-md glass-card shadow-lg z-10">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-display font-bold text-gray-950">
            Change Your <span className="gradient-text">Password</span>
          </CardTitle>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Current Password</label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  className="mt-1 w-full p-3 border border-gray-300/50 rounded-lg bg-white/50 backdrop-blur-sm"
                  required
                />
                <button 
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">New Password</label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="mt-1 w-full p-3 border border-gray-300/50 rounded-lg bg-white/50 backdrop-blur-sm"
                  required
                />
                <button 
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="mt-1 w-full p-3 border border-gray-300/50 rounded-lg bg-white/50 backdrop-blur-sm"
                  required
                />
                <button 
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            {error && <p className="text-red-600 text-sm">{error}</p>}
            {message && <p className="text-green-600 text-sm">{message}</p>}
            
            <Button 
              type="submit"
              disabled={!currentPassword || !newPassword || !confirmPassword || loading} 
              fullWidth
              className="bg-black hover:bg-gray-900 text-white font-medium !rounded-full shadow-md hover:shadow-lg transition-all duration-300"
            >
              {loading ? 'Updating...' : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}